import {Helmet} from 'react-helmet'
import {useEffect, useState} from 'react'
import {useTranslation} from '@/translations/i18n'
import {PageHeading} from '@/components/ui/page-atoms/PageAtoms'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {AlertTriangleIcon} from '@/components/ui/icon/Icon'
import {Badge} from '@/components/ui/badge/Badge'
import {ErrorBox} from '@/components/commons/error-box/ErrorBox'
import {EmptyState} from '@/components/commons/empty-state/EmptyState'
import {Table} from '@/components/commons/table/Table'
import {TableBodyCell, TableBodyCellText} from '@/components/ui/table-atoms/TableAtoms'
import {MoreActionsCell} from '@/components/ui/more-actions-cell/MoreActionsCell'
import {useParsedSearchParams} from '@/hooks/useParsedSearchParams'
import {PaginationResponse} from '@/types'
import {formatLocaleDate} from '@/dayjs'
import {capitalize} from '@/utilities/helpers'
import {useGetActivityReportHistory} from '@/features/accesses/activity-report/queries/useGetActivityReportHistory'
import {HttpGetActivityReportHistoryParams} from '@/features/accesses/activity-report/http/activityReport.http'
import {ActivityReportHistorySearchParams, ActivityReportsColumn} from '@/features/accesses/activity-report/types'
import {
    activityReportColumnToLabel,
    activityReportFileStatusToBadgeVariant,
    activityReportFileStatusToLabel,
    activityReportSectorToLabel,
    activityReportStatusToBadgeVariant,
    activityReportStatusToLabel
} from '@/features/accesses/activity-report/utils'
import {ActivityReportDialog} from '@/features/accesses/activity-report/components/activity-report-dialog/ActivityReportDialog'
import {ActivityReportRowMenu} from '@/features/accesses/activity-report/components/activity-report-row-menu/ActivityReportRowMenu'
import {ActivityReportExportCards} from '@/features/accesses/activity-report/components/activity-report-export-cards/ActivityReportExportCards'
import {useActivityReportExportSocket} from '@/features/accesses/activity-report/hooks/useActivityReportExportSocket'
import {StyledCreatedAtCell, StyledWrapper} from './style'

export const ActivityReportHistory = () => {
    const {t} = useTranslation()
    // Keep each row's embedded export-job state live as files are produced (FR-021).
    useActivityReportExportSocket()
    const {searchParams, setSearchParams} = useParsedSearchParams(ActivityReportHistorySearchParams)

    const apiParams: HttpGetActivityReportHistoryParams = {
        sector: searchParams.sector,
        status: searchParams.status,
        page: searchParams.page,
        pageSize: searchParams.limit
    }

    const [persistedPagination, setPersistedPagination] = useState<PaginationResponse>({
        total: 0,
        lastPage: 1,
        perPage: searchParams.limit,
        currentPage: searchParams.page
    })

    const {data, isPending, isError} = useGetActivityReportHistory(apiParams)
    const rows = data?.items ?? []

    useEffect(() => {
        if (data) setPersistedPagination(data.meta)
    }, [data])

    return (
        <StyledWrapper>
            <Helmet title={t('activity_report:history_meta_title')} />
            <PageHeading direction="column" gap={4}>
                <Flexbox fullWidth justify="space-between" align="center" gap={2}>
                    <h2>{t('activity_report:history_title')}</h2>
                    <ActivityReportDialog />
                </Flexbox>
                <ActivityReportExportCards />
            </PageHeading>
            {isError ? (
                <ErrorBox title={t('errors:default')} icon={<AlertTriangleIcon />} />
            ) : (
                <Table
                    emptyStateComponent={
                        <EmptyState
                            title={t('activity_report:history_empty_title')}
                            subtitle={t('activity_report:history_empty_subtitle')}
                            style={{border: 'none', height: '100%'}}
                        />
                    }
                    columnPinning={{
                        right: [ActivityReportsColumn.enum.moreActions]
                    }}
                    isLoading={isPending}
                    data={rows}
                    pagination={{
                        ...persistedPagination,
                        currentPage: searchParams.page,
                        limit: searchParams.limit,
                        onChangePage: page => setSearchParams({page}),
                        onChangeLimit: limit => setSearchParams({limit, page: 1})
                    }}
                    columns={[
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {capitalize(formatLocaleDate(dataItem.startDate, 'MMMM YYYY'))}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(activityReportColumnToLabel[ActivityReportsColumn.enum.period]),
                            id: ActivityReportsColumn.enum.period,
                            size: () => 200
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>
                                        {t(activityReportSectorToLabel[dataItem.sector])}
                                    </TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(activityReportColumnToLabel[ActivityReportsColumn.enum.sector]),
                            id: ActivityReportsColumn.enum.sector,
                            size: () => 160
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.accessesCount}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(activityReportColumnToLabel[ActivityReportsColumn.enum.accesses]),
                            id: ActivityReportsColumn.enum.accesses,
                            size: () => 120
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <TableBodyCellText>{dataItem.absencesCount}</TableBodyCellText>
                                </TableBodyCell>
                            ),
                            header: () => t(activityReportColumnToLabel[ActivityReportsColumn.enum.absences]),
                            id: ActivityReportsColumn.enum.absences,
                            size: () => 120
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <Badge variant={activityReportStatusToBadgeVariant[dataItem.status]} size="sm">
                                        {t(activityReportStatusToLabel[dataItem.status])}
                                    </Badge>
                                </TableBodyCell>
                            ),
                            header: () => t(activityReportColumnToLabel[ActivityReportsColumn.enum.status]),
                            id: ActivityReportsColumn.enum.status,
                            size: () => 160
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <Badge
                                        variant={activityReportFileStatusToBadgeVariant(dataItem.exportJob?.status)}
                                        size="sm"
                                    >
                                        {t(activityReportFileStatusToLabel(dataItem.exportJob?.status))}
                                    </Badge>
                                </TableBodyCell>
                            ),
                            header: () => t(activityReportColumnToLabel[ActivityReportsColumn.enum.file]),
                            id: ActivityReportsColumn.enum.file,
                            size: () => 160
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <TableBodyCell {...forwardedProps}>
                                    <StyledCreatedAtCell>
                                        <strong>{formatLocaleDate(dataItem.createdAt, 'lll')}</strong>
                                    </StyledCreatedAtCell>
                                </TableBodyCell>
                            ),
                            header: () => t(activityReportColumnToLabel[ActivityReportsColumn.enum.createdAtAndAuthor]),
                            id: ActivityReportsColumn.enum.createdAtAndAuthor,
                            size: () => 220
                        },
                        {
                            cell: ({dataItem, forwardedProps}) => (
                                <MoreActionsCell {...forwardedProps}>
                                    <ActivityReportRowMenu report={dataItem} />
                                </MoreActionsCell>
                            ),
                            header: () => undefined,
                            id: ActivityReportsColumn.enum.moreActions,
                            size: () => 56
                        }
                    ]}
                />
            )}
        </StyledWrapper>
    )
}
